// ─── Tenant feature flags (plan + overrides) ─────────────────────────────────
let effectiveFeatures: Record<string, boolean> = {};
let planFeatures: Record<string, boolean> = {};

export function setEffectiveFeatures(features: Record<string, boolean>): void {
  effectiveFeatures = { ...features };
}

export function getEffectiveFeatures(): Record<string, boolean> {
  return effectiveFeatures;
}

export function setPlanFeatures(features: Record<string, boolean>): void {
  planFeatures = { ...features };
}

export function getPlanFeatures(): Record<string, boolean> {
  return planFeatures;
}

export function isFeatureEnabled(code: string): boolean {
  return Boolean(effectiveFeatures[code]);
}

/** Returns true only if the tenant's plan includes this feature (ignores per-tenant overrides). */
export function isFeatureInPlan(code: string): boolean {
  return Boolean(planFeatures[code]);
}

// ─── RBAC permission store ────────────────────────────────────────────────────
let userPermissions: string[] = [];
let userHierarchyLevel: number | null = null;
let userAssignedRole: string | null = null;

export function setUserPermissions(permissions: string[]): void {
  userPermissions = [...permissions];
}

export function getUserPermissions(): string[] {
  return userPermissions;
}

export function setUserHierarchyLevel(level: number | null): void {
  userHierarchyLevel = level;
}

export function getUserHierarchyLevel(): number | null {
  return userHierarchyLevel;
}

export function setUserAssignedRole(role: string | null): void {
  userAssignedRole = role;
}

export function getUserAssignedRole(): string | null {
  return userAssignedRole;
}

/**
 * Check whether the current user has a specific permission code.
 * Falls back to coarse role check from authService if no permissions are loaded yet.
 */
export function hasPermission(code: string): boolean {
  if (userPermissions.length > 0) {
    return userPermissions.includes(code);
  }
  // Fallback: use coarse role stored in localStorage
  try {
    const userStr = localStorage.getItem('auth_user');
    if (!userStr) return false;
    const user = JSON.parse(userStr);
    const role = (user?.role || '').toUpperCase();
    if (role === 'OWNER') return true;
    if (role === 'MANAGER') return !code.endsWith('.delete');
  } catch {
    // ignore
  }
  return false;
}

/**
 * Check if the current user has at least one permission matching <module>.view.
 * Used for sidebar module visibility gating.
 */
export function hasModuleAccess(module: string): boolean {
  return hasPermission(`${module}.view`);
}

/** Apply the standard tenant-feature AND module-view rule used by sidebar items. */
export function hasFeatureModuleAccess(featureCode: string, module: string): boolean {
  if (!isFeatureEnabled(featureCode)) return false;
  if (userPermissions.length === 0) return true;
  return hasModuleAccess(module);
}

/** Clear RBAC store on logout */
export function clearUserPermissions(): void {
  userPermissions     = [];
  userHierarchyLevel  = null;
  userAssignedRole    = null;
}
